import { useMemo } from 'react';
import { CardTypesEnum, HealthPlanStatuses, useQuery } from '@common-fe/common-fe';

import { api } from '@/api';
import { OrganizationTypes } from '@/common/constants';
import { Option } from '@/common/types';
import { API_URL } from '@/dtos';
import { useCurrentOrganization } from '@/modules/core/hooks';
import { HealthPlan, HealthPlanFieldState } from '@/modules/HealthPlan/HealthPlan.types';
import getIdsFromPath from '@/utils/modifiers/getIdsFromPath';

// import { useIsOrganizationType } from '@/modules/user/hooks';

export const getTemplatesList =
  (status?: HealthPlanStatuses, path?: string, orgId?: string, page?: number, name?: string, id?: string) =>
  () =>
    api.get(API_URL.HEALTH_PLANS_TEMPLATES, {
      params: {
        organization_id: status !== HealthPlanStatuses.ACTIVE ? orgId : undefined,
        organization_path: status === HealthPlanStatuses.ACTIVE && path ? path : undefined,
        plan_status: status,
        name,
        id,
        size: 10000,
      },
    });

export const HEALTH_TEMPLATES_LIST_QUERY_KEY = 'getHealthTemplatesList';
type ExpandedOption = Option & {
  accountTypes?: string[];
  accountTypeState?: HealthPlanFieldState;
  name?: string;
  nameState?: HealthPlanFieldState;
};

interface Params {
  status?: HealthPlanStatuses;
  refetchOnMount?: boolean;
  activePage?: number;
  searchTemplateString?: string;
  id?: string;
}
export const useHealthTemplatesListQuery = (params: Params) => {
  const {
    observingOrganization: { type, id: observingOrganizationId, path },
  } = useCurrentOrganization();

  const { employerId } = getIdsFromPath(path);

  const isSubsidiary = useMemo(() => type === OrganizationTypes.subsidiary, [type]);

  const organizationId = useMemo(
    () => (isSubsidiary ? employerId : observingOrganizationId),
    [isSubsidiary, employerId, observingOrganizationId]
  );

  const currentId = useMemo(() => params?.id || organizationId || '0', [params?.id, organizationId]);
  const { isLoading, error, data, isSuccess, isError, refetch } = useQuery(
    [
      HEALTH_TEMPLATES_LIST_QUERY_KEY,
      params.status,
      path,
      params.activePage,
      params.searchTemplateString,
      params.id,
    ],
    getTemplatesList(
      params.status || HealthPlanStatuses.DRAFT,
      path,
      currentId,
      params.activePage,
      params.searchTemplateString,
      params.id
    ),
    {
      refetchOnMount: params.refetchOnMount,
      enabled: params.status === HealthPlanStatuses.ACTIVE ? !!path : true,
    }
  );

  const formattedTemplatesOptions: ExpandedOption[] = useMemo(() => {
    const list = data?.data || [];

    return list?.map(
      (item): ExpandedOption => ({
        key: `${item.id}`,
        value: item?.name?.name || '',
        title: item?.name?.name || '',
        accountTypes: item?.health_plan_types?.health_plan_types as unknown as string[],
        accountTypeState: item?.health_plan_types?.health_plan_type_state as unknown as HealthPlanFieldState,
        nameState: item?.name?.name_state as unknown as HealthPlanFieldState,
      })
    );
  }, [data]);

  const formattedTemplates: HealthPlan[] = useMemo(() => {
    const list = data?.data || [];

    return list.map(
      (item): HealthPlan => ({
        id: `${item.id}`,
        name: item?.name?.name,
        // TODO: this field is missing
        // @ts-ignore
        nameState: item?.name?.name_state as unknown as HealthPlanFieldState,
        types: item?.health_plan_types?.health_plan_types as unknown as CardTypesEnum[],
        typeState: item?.health_plan_types?.health_plan_type_state as unknown as HealthPlanFieldState,
        status: item.plan_status as HealthPlanStatuses,
        planYearId: item.plan_year?.id || '',
        planYearName: item.plan_year?.name || '',
      })
    );
  }, [data]);

  return {
    data: formattedTemplates,
    error,
    isError,
    isLoading,
    isSuccess,
    options: formattedTemplatesOptions,
    refetch,
  };
};
export default useHealthTemplatesListQuery;
